import {
  useInfiniteQuery,
  QueryFunctionContext,
  UseInfiniteQueryOptions,
  useQueries,
} from '@tanstack/react-query';
import FeedService from '../../services/feed';
import useFeatureFlag from '../use-feature-flag';
import { Feature } from '../../models/features/features';
import { Content } from '../../models/content/types';
import { contentOperations } from '../../models/content';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo, useRef } from 'react';

//this interface is in desperate need of a refactor to separate out the use cases of the different feed endpoints. (search, homepage, regular feed, profile feeds).
//I made an attempt, but I think it will also require API changes to make it clean. I want to avoid having many type definitions in various stages of deprecation for an api interface.

/**
 * Interface for content feed options
 */
export interface ContentFeedOptions {
  query?: string;
  filter?: string;
  channelId?: string;
  advocateId?: string;
  advocateFilter?: string;
  perPage?: number;
  includeTypes?: string[];
  initiativeId?: string;
  pinned?: boolean;
  queryEnabled?: boolean;
  homepageModuleParams?: HomepageModuleParams; //these are only functional if query is undefined (as that will hit the /search endpoint which does not support them, same with many of the other params here.)
}

export interface HomepageModuleParams {
  homepage_content_channel_ids?: number[];
  homepage_initiative_ids?: number[];
  only_followed_channels?: boolean;
  content_type?: 'admin-generated' | 'user-generated';
}

/**
 * Interface for content feed response metadata
 */
export interface ContentFeedMeta {
  page: {
    number: number;
    size: number;
  };
  total: number;
  total_pages: number;
}

/**
 * Interface for content feed response
 */
export interface ContentFeedResponse {
  feed?: Array<{ attributes: Content }>;
  contents?: Array<{ attributes: Content }>;
  meta: ContentFeedMeta;
}

/**
 * Interface for paginated content feed data
 */
export interface ContentFeedPage {
  items: Content[];
  nextPage: number | null;
  meta: ContentFeedMeta;
}

/**
 * Max display contents configuration
 */
export const MAX_DISPLAY_CONTENTS_MAP: MaxDisplayContents = {
  hero: 4,
  grid: 6,
  gallery: 9,
  row: 3,
};

/**
 * Max display contents configuration
 */
export type MaxDisplayContents = {
  hero: number;
  grid: number;
  gallery: number;
  row: number;
};

export const getPerPage = (
  order: number,
  displayType: 'grid' | 'hero' | 'gallery' | 'row'
) => {
  return (
    Math.max(...Object.values(MAX_DISPLAY_CONTENTS_MAP)) * order +
    MAX_DISPLAY_CONTENTS_MAP[displayType]
  );
};

/**
 * Base query key for content feed
 */
export const CONTENT_FEED_QUERY_KEY = ['contentFeed'] as const;

/**
 * Generates a consistent query key based on all the feed options
 *
 * @param options - Options for the content feed and feature flags
 * @returns An array to be used as the query key
 */
const queryKey = ({
  query,
  filter,
  channelId,
  advocateId,
  advocateFilter,
  perPage,
  includeTypes,
  initiativeId,
  pinned,
  tagUserEnabled,
  advancedSearchEnabled,
  homepageModuleParams,
}: ContentFeedOptions & {
  tagUserEnabled: boolean;
  advancedSearchEnabled: boolean;
}): string[] => {
  return [
    CONTENT_FEED_QUERY_KEY[0],
    query || '',
    filter || '',
    channelId || '',
    advocateId || '',
    advocateFilter || '',
    perPage?.toString() || '',
    includeTypes?.join(',') || '',
    initiativeId || '',
    pinned ? 'pinned' : '',
    tagUserEnabled ? 'tagEnabled' : 'tagDisabled',
    advancedSearchEnabled ? 'advancedSearch' : 'basicSearch',
    homepageModuleParams ? JSON.stringify(homepageModuleParams) : '',
  ];
};

/**
 * Custom hook to fetch infinite content feed with pagination support
 *
 * This hook uses React Query's infinite query capabilities to fetch paginated content feed data.
 * It automatically manages the pagination state and provides methods to fetch more data.
 *
 * It also maintains compatibility with the existing Redux store by dispatching content items.
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage,
 *   isLoading,
 *   isError,
 *   error
 * } = useContentFeedQuery({ channelId: "123" });
 *
 * // Access all feed items as a flat array
 * const allItems = flattenContentFeedPages(data?.pages);
 *
 * // Load more items when needed
 * const loadMore = () => {
 *   if (hasNextPage && !isFetchingNextPage) {
 *     fetchNextPage();
 *   }
 * };
 * ```
 *
 * @param options - Options for the content feed
 * @param queryOptions - Optional React Query options
 * @returns Infinite query result with content feed data and methods to fetch more
 */
export const useContentFeedQuery = (
  {
    query,
    filter,
    channelId,
    advocateId,
    advocateFilter,
    perPage = 20,
    includeTypes,
    initiativeId,
    pinned,
    homepageModuleParams,
    queryEnabled = true,
  }: ContentFeedOptions = {},
  queryOptions?: Omit<
    UseInfiniteQueryOptions<
      ContentFeedPage,
      Error,
      ContentFeedPage,
      ContentFeedPage,
      string[]
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam'
  >
) => {
  const tagUserEnabled = useFeatureFlag(Feature.TAG_USER_ENABLED);
  const advancedSearchEnabled = useFeatureFlag(Feature.IMPROVED_SEARCH);
  const dispatch = useDispatch();
  const queryObject = useInfiniteQuery<
    ContentFeedPage,
    Error,
    ContentFeedPage,
    string[]
  >({
    queryKey: queryKey({
      query,
      filter,
      channelId,
      advocateId,
      advocateFilter,
      perPage,
      includeTypes,
      initiativeId,
      pinned,
      tagUserEnabled: !!tagUserEnabled,
      advancedSearchEnabled: !!advancedSearchEnabled,
      homepageModuleParams,
    }),
    enabled: queryEnabled,
    queryFn: async ({ pageParam }: QueryFunctionContext<string[], number>) => {
      const currentPage = pageParam || 1;
      const feedService = new FeedService({
        query,
        filter,
        channelId,
        advocateId,
        advocateFilter,
        includeTypes,
        initiativeId,
        mention_format: tagUserEnabled ? 'with_metadata' : undefined,
        useLegacyMetadata: !advancedSearchEnabled,
        homepageModuleParams,
      });

      const response = await feedService.fetch({
        page: currentPage,
        perPage,
        pinned,
      });

      const items = (response.feed || response.contents)
        .map((fi) => fi.attributes)
        .filter((fi) =>
          Object.prototype.hasOwnProperty.call(fi, 'content_type')
        );

      if (perPage && items.length > perPage) {
        items.splice(perPage);
      }

      const meta = response.meta;

      let nextPage: number | null;
      if (response.meta.total_pages) {
        nextPage =
          currentPage < response.meta.total_pages ? currentPage + 1 : null;
      } else {
        // not all feeds seem to provide total_pages in meta.
        // The legacy behavior was to check if we got a full page of results to determine if there was a next page, using that here as a fallback. (see deprecated useContentFeed behavior)
        nextPage = items.length >= perPage ? currentPage + 1 : null;
      }

      return {
        items,
        nextPage,
        meta,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    ...queryOptions,
  });

  // Dispatch contents to Redux store
  // This is a temporary solution to ensure the contents are available in the Redux store
  // This should be removed after migrating from Redux to React Query

  // Note: There are scenarios where the queryFn may have not been called but queryObject.data has
  // data from cache ( local storage) so it is important that the dispatch is done outside of the queryFn
  useEffect(() => {
    if (queryObject.data) {
      dispatch(
        contentOperations.addContents(
          queryObject.data.pages.flatMap((page) => page.items)
        )
      );
    }
  }, [queryObject.data, dispatch]);

  return {
    ...queryObject,
    rawQueryData: queryObject.data,
    data: queryObject.data?.pages.flatMap((page) => page.items),
  };
};

/**
 * Configuration for a single content feed section query
 */
export type ContentFeedSectionConfig = {
  feed: string;
  filterId: number;
  displayType: 'grid' | 'hero' | 'gallery' | 'row';
  order: number;
  enabled?: boolean;
};

/**
 * Helper function to determine filter value based on feed type
 */
const getFilterForFeed = (feed: string): string | undefined => {
  if (feed === 'community') return 'community_images';
  if (feed === 'channel') return undefined;
  return feed;
};

/**
 * Hook that creates content feed queries for multiple feed sections
 *
 * This hook batches multiple content feed queries using useQueries,
 * allowing parallel fetching of multiple feeds with proper caching and state management.
 *
 * @param feedSections - Array of feed section configurations
 * @returns Array of query results, one for each section
 */
export const useContentFeedsBySectionsQuery = (
  feedSections: ContentFeedSectionConfig[]
): Array<{
  data: Content[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}> => {
  const dispatch = useDispatch();
  const tagUserEnabled = useFeatureFlag(Feature.TAG_USER_ENABLED);
  const advancedSearchEnabled = useFeatureFlag(Feature.IMPROVED_SEARCH);
  const customHomepageEnabled = useFeatureFlag(Feature.CUSTOM_HOMEPAGE_ENABLED);

  // Create queries array with proper dependencies
  const queriesConfig = useMemo(
    () =>
      feedSections.map(({ feed, filterId, displayType, order, enabled }) => {
        let includeTypes: string[] | undefined = undefined;
        if (!customHomepageEnabled) {
          includeTypes = displayType === 'gallery' ? ['image'] : undefined;
        }

        const perPage = getPerPage(order, displayType);
        const filter = getFilterForFeed(feed);

        return {
          queryKey: [
            CONTENT_FEED_QUERY_KEY[0],
            'sections',
            feed,
            filterId,
            order,
            perPage,
            filter || '',
            displayType,
            tagUserEnabled,
            advancedSearchEnabled,
            customHomepageEnabled,
          ] as const,
          queryFn: async () => {
            const feedService = new FeedService({
              filter: filter,
              channelId: feed === 'channel' ? filterId.toString() : undefined,
              initiativeId:
                feed === 'initiative' ? filterId.toString() : undefined,
              includeTypes,
              mention_format: tagUserEnabled ? 'with_metadata' : undefined,
              useLegacyMetadata: !advancedSearchEnabled,
            });

            const response = await feedService.fetch({
              page: 1,
              perPage: perPage,
            });

            const items = (response.feed || response.contents)
              .map((fi) => fi.attributes)
              .filter((fi) =>
                Object.prototype.hasOwnProperty.call(fi, 'content_type')
              );

            return items.slice(0, perPage);
          },
          enabled: enabled !== undefined ? enabled : true,
        };
      }),
    [feedSections, tagUserEnabled, advancedSearchEnabled, customHomepageEnabled]
  );

  // Use useQueries to run all queries in parallel
  const queries = useQueries({
    queries: queriesConfig,
  });

  // Dispatch contents to Redux store (matching useContentFeedQuery behavior)
  // Use a ref to track previous data to avoid unnecessary dispatches
  const previousDataRef = useRef<string>('');
  useEffect(() => {
    const allContents = queries
      .map((query) => query.data as Content[] | undefined)
      .filter((data): data is Content[] => Array.isArray(data))
      .flat();

    // Create a stable key from the data to avoid unnecessary dispatches
    const dataKey = JSON.stringify(allContents.map((c) => c.id));
    if (allContents.length > 0 && dataKey !== previousDataRef.current) {
      previousDataRef.current = dataKey;
      dispatch(contentOperations.addContents(allContents));
    }
  }, [queries, dispatch]);

  // Transform the results to match the expected return type
  // Memoize based on actual data values to prevent unnecessary re-renders
  const queriesIdentifier = useMemo(
    () =>
      queries
        .map((q) => {
          const dataIds =
            (q.data as Content[] | undefined)?.map((c) => c.id).join(',') || '';
          return `${dataIds}|${q.isLoading}|${q.isSuccess}|${q.isError}`;
        })
        .join('||'),
    [queries]
  );

  return useMemo(
    () =>
      queries.map((query) => ({
        data: (query.data as Content[]) || [],
        isLoading: query.isLoading,
        isSuccess: query.isSuccess,
        isError: query.isError,
        error: query.error as Error | null,
        refetch: query.refetch,
      })),
    // Only depend on queriesKey - the actual queries array reference changes but content doesn't
    // queriesKey is derived from queries, so this is safe
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queriesIdentifier]
  );
};
